/*
  Landing nueva (vault 346, §5): las solicitudes de Mini Prixia. El contrato completo está al final de la
  nota 346 («Construido: servidor y limpieza»). Las listas son las mismas que valida el servidor
  (`solicitudes-mini-prixia.service.ts`): un valor fuera de lista da 400.
*/

export const RUBROS_DE_LA_SOLICITUD = [
  { valor: 'tecnologia', rotulo: 'Tecnología y celulares' },
  { valor: 'hogar', rotulo: 'Hogar y electrodomésticos' },
  { valor: 'farmacia', rotulo: 'Farmacia' },
  { valor: 'ferreteria', rotulo: 'Ferretería y construcción' },
  { valor: 'supermercado', rotulo: 'Supermercado' },
  { valor: 'moda-y-deporte', rotulo: 'Moda, calzado y deporte' },
  { valor: 'perfumeria-y-cosmetica', rotulo: 'Perfumería y cosmética' },
  { valor: 'distribuidora', rotulo: 'Distribuidora o mayorista' },
  { valor: 'otro', rotulo: 'Otro' },
] as const;

export const TAMANOS_DE_CATALOGO = [
  { valor: 'hasta-500', rotulo: 'Hasta 500 productos' },
  { valor: '500-a-5000', rotulo: 'De 500 a 5.000 productos' },
  { valor: 'mas-de-5000', rotulo: 'Más de 5.000 productos' },
] as const;

export const ASUNTOS_DE_CONTACTO = [
  { valor: 'demo-producto', rotulo: 'Pedir mi Mini Prixia' },
  { valor: 'consulta-general', rotulo: 'Consulta general' },
  { valor: 'soporte-tecnico', rotulo: 'Soporte técnico' },
  { valor: 'alianza', rotulo: 'Alianzas' },
  { valor: 'otro', rotulo: 'Otro' },
] as const;

export const ORIGENES_DE_LA_SOLICITUD = [
  'landing-hero',
  'landing-final',
  'landing-modal',
  'mini-prixia',
  'contacto',
  'registro',
] as const;

export type Rubro = (typeof RUBROS_DE_LA_SOLICITUD)[number]['valor'];
export type TamanoDeCatalogo = (typeof TAMANOS_DE_CATALOGO)[number]['valor'];
export type AsuntoDeContacto = (typeof ASUNTOS_DE_CONTACTO)[number]['valor'];
export type OrigenDeLaSolicitud = (typeof ORIGENES_DE_LA_SOLICITUD)[number];

/** El cuerpo de `POST /api/public/solicitudes-mini-prixia`. */
export interface NuevaSolicitud {
  nombre: string;
  correo: string;
  empresa: string;
  aceptoContacto: true;
  cargo?: string;
  telefono?: string;
  rubro?: Rubro | '';
  tamanoDeCatalogo?: TamanoDeCatalogo | '';
  queQuiereResolver?: string;
  origen: OrigenDeLaSolicitud;
  asunto?: AsuntoDeContacto | '';
  /** Trampa: el formulario la esconde; siempre vacía. */
  sitioWeb: string;
  /** Milisegundos desde que se abrió el formulario (una duración, no la hora del navegador). */
  msEnElFormulario: number;
}

/** Lo que ve el panel. «Activada» lo deduce el servidor (la invitación ya tuvo su alta). */
export type EstadoDeSolicitud = 'NUEVA' | 'EN_REVISION' | 'INVITADA' | 'ACTIVADA' | 'DESCARTADA';

export interface FilaDeSolicitud {
  id: string;
  createdAt: string;
  nombre: string | null;
  correo: string | null;
  empresa: string | null;
  cargo: string | null;
  rubro: string | null;
  tamanoDeCatalogo: string | null;
  origen: string;
  asunto: string | null;
  estado: EstadoDeSolicitud;
  vecesPedida: number;
  correoPersonal: boolean;
  datosBorrados: boolean;
}

export interface FichaDeSolicitud extends FilaDeSolicitud {
  telefono: string | null;
  queQuiereResolver: string | null;
  aceptoContacto: boolean;
  motivoDescarte: string | null;
  notaInterna: string | null;
  revisadaPorId: string | null;
  revisadaEn: string | null;
  datosBorradosEn: string | null;
  invitacion: {
    id: string;
    codigo: string;
    /** Relativo: `/invitacion/<codigo>`. */
    enlace: string;
    venceEn: string | null;
    altasUsadas: number;
    estado: 'ACTIVA' | 'PAUSADA' | 'VENCIDA' | 'AGOTADA' | 'NO_EXISTE';
  } | null;
  alta: {
    empresa: string;
    tenantId: string | null;
    createdAt: string;
    empresaExiste: boolean;
  } | null;
}

export interface ListaDeSolicitudes {
  filas: FilaDeSolicitud[];
  total: number;
  pagina: number;
  porPagina: number;
  /** Cuántas hay en NUEVA (el distintivo de la pestaña). */
  nuevas: number;
}

export interface CambioDeSolicitud {
  estado?: 'NUEVA' | 'EN_REVISION' | 'DESCARTADA';
  motivoDescarte?: string | null;
  notaInterna?: string | null;
}

export interface InvitacionDeLaSolicitud {
  creada: boolean;
  solicitud: FichaDeSolicitud;
  invitacion: NonNullable<FichaDeSolicitud['invitacion']>;
  enlace: string;
  correoSugerido: { asunto: string; cuerpo: string };
}

export const rotuloDeRubro = (valor: string | null): string | null =>
  valor ? (RUBROS_DE_LA_SOLICITUD.find((r) => r.valor === valor)?.rotulo ?? valor) : null;

export const rotuloDeTamano = (valor: string | null): string | null =>
  valor ? (TAMANOS_DE_CATALOGO.find((t) => t.valor === valor)?.rotulo ?? valor) : null;

export const rotuloDeAsunto = (valor: string | null): string | null =>
  valor ? (ASUNTOS_DE_CONTACTO.find((a) => a.valor === valor)?.rotulo ?? valor) : null;
